import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  X, 
  TrendingUp, 
  TrendingDown, 
  Activity, 
  AlertTriangle,
  BarChart3,
  Info,
  Signal,
  Zap
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { stockService } from '../services/stockService';

const TechnicalAnalysis = ({ symbol, onClose, currentPrice, historicalData }) => {
  const { t } = useLanguage();
  const [technicalData, setTechnicalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTooltip, setShowTooltip] = useState(null);
  const [analysisData, setAnalysisData] = useState([]);
  const [fetchingFallback, setFetchingFallback] = useState(false);
  const [fallbackSymbol, setFallbackSymbol] = useState(null);

  useEffect(() => {
    const safeData = Array.isArray(historicalData) ? historicalData : [];
    setAnalysisData(safeData);

    if (symbol && safeData.length > 0 && safeData.length < 20 && fallbackSymbol !== symbol) {
      setFallbackSymbol(symbol);
      setFetchingFallback(true);
      stockService.getHistoricalData(symbol, 'daily', 'all')
        .then((data) => {
          if (Array.isArray(data) && data.length) {
            setAnalysisData(data);
          }
        })
        .catch((err) => {
          console.error('Failed to fetch extended historical data:', err);
        })
        .finally(() => {
          setFetchingFallback(false);
        });
    }
  }, [symbol, historicalData, fallbackSymbol]);

  useEffect(() => {
    const calculateTechnicalIndicators = () => {
      try {
        setLoading(true);
        
        if (!analysisData || !Array.isArray(analysisData) || analysisData.length === 0) {
          setError('No historical data available for technical analysis. Please ensure the stock data is loaded properly.');
          return;
        }

        if (analysisData.length < 20) {
          if (fetchingFallback) {
            setError(null);
            return;
          }
          setError('Insufficient historical data for technical analysis. Need at least 20 data points.');
          return;
        }


        const prices = analysisData.map(d => d.close);
        const volumes = analysisData.map(d => d.volume);
        const dates = analysisData.map(d => d.date);

        // Calculate Moving Averages
        const sma20 = calculateSMA(prices, 20);
        const sma50 = calculateSMA(prices, 50);
        const sma200 = calculateSMA(prices, 200);
        
        const ema12 = calculateEMA(prices, 12);
        const ema26 = calculateEMA(prices, 26);

        // Calculate RSI
        const rsi = calculateRSI(prices, 14);

        // Calculate MACD
        const macd = calculateMACD(ema12, ema26);

        // Calculate Bollinger Bands
        const bollingerBands = calculateBollingerBands(prices, sma20, 20);

        // Calculate Volume indicators
        const volumeMA = calculateSMA(volumes, 20);

        // Generate signals
        const signals = generateSignals({
          prices,
          sma20,
          sma50,
          sma200,
          rsi: rsi[rsi.length - 1],
          macd: macd[macd.length - 1],
          bollingerBands: bollingerBands[bollingerBands.length - 1],
          currentPrice
        });

        setTechnicalData({
          currentPrice,
          indicators: {
            sma20: sma20.length > 0 ? sma20[sma20.length - 1] : null,
            sma50: sma50.length > 0 ? sma50[sma50.length - 1] : null,
            sma200: sma200.length > 0 ? sma200[sma200.length - 1] : null,
            ema12: ema12[ema12.length - 1],
            ema26: ema26[ema26.length - 1],
            rsi: rsi[rsi.length - 1],
            macd: macd[macd.length - 1],
            bollingerBands: bollingerBands[bollingerBands.length - 1],
            volumeMA: volumeMA[volumeMA.length - 1],
            currentVolume: volumes[volumes.length - 1]
          },
          signals,
          trends: analyzeTrends({ sma20, sma50, sma200, prices })
        });
        
        setError(null);
      } catch (err) {
        setError('Failed to calculate technical indicators: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    if (symbol && analysisData) {
      calculateTechnicalIndicators();
    }
  }, [symbol, analysisData, currentPrice, fetchingFallback]);


  // Technical Indicator Calculations
  const calculateSMA = (prices, period) => {
    const sma = [];
    for (let i = period - 1; i < prices.length; i++) {
      const sum = prices.slice(i - period + 1, i + 1).reduce((a, b) => a + b, 0);
      sma.push(sum / period);
    }
    return sma;
  };

  const calculateEMA = (prices, period) => {
    const ema = [];
    const multiplier = 2 / (period + 1);
    ema[0] = prices[0];
    
    for (let i = 1; i < prices.length; i++) {
      ema[i] = (prices[i] * multiplier) + (ema[i - 1] * (1 - multiplier));
    }
    return ema;
  };

  const calculateRSI = (prices, period) => {
    const gains = [];
    const losses = [];
    
    for (let i = 1; i < prices.length; i++) {
      const change = prices[i] - prices[i - 1];
      gains.push(change > 0 ? change : 0);
      losses.push(change < 0 ? Math.abs(change) : 0);
    }
    
    const rsi = [];
    for (let i = period - 1; i < gains.length; i++) {
      const avgGain = gains.slice(i - period + 1, i + 1).reduce((a, b) => a + b, 0) / period;
      const avgLoss = losses.slice(i - period + 1, i + 1).reduce((a, b) => a + b, 0) / period;
      
      if (avgLoss === 0) {
        rsi.push(100);
      } else {
        const rs = avgGain / avgLoss;
        rsi.push(100 - (100 / (1 + rs)));
      }
    }
    return rsi;
  };

  const calculateMACD = (ema12, ema26) => {
    const macdLine = [];
    for (let i = 0; i < Math.min(ema12.length, ema26.length); i++) {
      macdLine.push(ema12[i] - ema26[i]);
    }
    
    const signalLine = calculateEMA(macdLine, 9);
    const histogram = [];
    
    for (let i = 0; i < Math.min(macdLine.length, signalLine.length); i++) {
      histogram.push(macdLine[i] - signalLine[i]);
    }
    
    return macdLine.map((macd, i) => ({
      macd,
      signal: signalLine[i] || 0,
      histogram: histogram[i] || 0
    }));
  };

  const calculateBollingerBands = (prices, sma, period) => {
    const bands = [];
    for (let i = period - 1; i < prices.length; i++) {
      const slice = prices.slice(i - period + 1, i + 1);
      const mean = sma[i - period + 1];
      const variance = slice.reduce((sum, price) => sum + Math.pow(price - mean, 2), 0) / period;
      const stdDev = Math.sqrt(variance);
      
      bands.push({
        upper: mean + (stdDev * 2),
        middle: mean,
        lower: mean - (stdDev * 2)
      });
    }
    return bands;
  };

  const generateSignals = ({ prices, sma20, sma50, sma200, rsi, macd, bollingerBands, currentPrice }) => {
    const signals = [];
    
    // Moving Average Signals
    if (currentPrice > sma20[sma20.length - 1] && currentPrice > sma50[sma50.length - 1]) {
      signals.push({ type: 'bullish', indicator: 'Moving Averages', messageKey: 'priceAboveMA' });
    } else if (currentPrice < sma20[sma20.length - 1] && currentPrice < sma50[sma50.length - 1]) {
      signals.push({ type: 'bearish', indicator: 'Moving Averages', messageKey: 'priceBelowMA' });
    }
    
    // Golden Cross / Death Cross (only if we have enough data for SMA 200)
    if (sma50.length >= 2 && sma200.length >= 2) {
      if (sma50[sma50.length - 1] > sma200[sma200.length - 1] && sma50[sma50.length - 2] <= sma200[sma200.length - 2]) {
        signals.push({ type: 'bullish', indicator: 'Golden Cross', messageKey: 'goldenCrossSignal' });
      } else if (sma50[sma50.length - 1] < sma200[sma200.length - 1] && sma50[sma50.length - 2] >= sma200[sma200.length - 2]) {
        signals.push({ type: 'bearish', indicator: 'Death Cross', messageKey: 'deathCrossSignal' });
      }
    }
    
    // RSI Signals
    if (rsi > 70) {
      signals.push({ type: 'bearish', indicator: 'RSI', messageKey: 'rsiOverbought' });
    } else if (rsi < 30) {
      signals.push({ type: 'bullish', indicator: 'RSI', messageKey: 'rsiOversold' });
    }
    
    // MACD Signals
    if (macd.macd > macd.signal && macd.histogram > 0) {
      signals.push({ type: 'bullish', indicator: 'MACD', messageKey: 'macdBullish' });
    } else if (macd.macd < macd.signal && macd.histogram < 0) {
      signals.push({ type: 'bearish', indicator: 'MACD', messageKey: 'macdBearish' });
    }
    
    // Bollinger Bands Signals
    if (currentPrice > bollingerBands.upper) {
      signals.push({ type: 'bearish', indicator: 'Bollinger Bands', messageKey: 'bbUpperBreakout' });
    } else if (currentPrice < bollingerBands.lower) {
      signals.push({ type: 'bullish', indicator: 'Bollinger Bands', messageKey: 'bbLowerBounce' });
    }
    
    return signals;
  };

  const analyzeTrends = ({ sma20, sma50, sma200, prices }) => {
    const latest = prices.length - 1;
    const current = prices[latest];
    const sma20Current = sma20.length > 0 ? sma20[sma20.length - 1] : null;
    const sma50Current = sma50.length > 0 ? sma50[sma50.length - 1] : null;
    const sma200Current = sma200.length > 0 ? sma200[sma200.length - 1] : null;
    
    let shortTerm = 'neutral';
    let mediumTerm = 'neutral';
    let longTerm = 'neutral';
    
    // Short-term trend (based on 20 MA)
    if (current > sma20Current && sma20Current > sma20[sma20.length - 5]) {
      shortTerm = 'bullish';
    } else if (current < sma20Current && sma20Current < sma20[sma20.length - 5]) {
      shortTerm = 'bearish';
    }
    
    // Medium-term trend (based on 50 MA)
    if (current > sma50Current && sma50Current > sma50[sma50.length - 10]) {
      mediumTerm = 'bullish';
    } else if (current < sma50Current && sma50Current < sma50[sma50.length - 10]) {
      mediumTerm = 'bearish';
    }
    
    // Long-term trend (based on 200 MA) - only if we have SMA 200 data
    if (sma200Current !== null) {
      if (current > sma200Current) {
        longTerm = 'bullish';
      } else if (current < sma200Current) {
        longTerm = 'bearish';
      }
    }
    
    return { shortTerm, mediumTerm, longTerm };
  };

  const formatNumber = (value, decimals = 2) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return value.toFixed(decimals);
  };

  const getSignalColor = (type) => {
    return type === 'bullish' ? '#10b981' : '#ef4444';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'bullish') return <TrendingUp size={16} color="#10b981" />;
    if (trend === 'bearish') return <TrendingDown size={16} color="#ef4444" />;
    return <Activity size={16} color="#6b7280" />;
  };

  const tooltipInfo = {
    sma: "Simple Moving Average smooths price data by calculating the average price over a specific period. Used to identify trend direction.",
    ema: "Exponential Moving Average gives more weight to recent prices, making it more responsive to new information than SMA.",
    rsi: "Relative Strength Index measures the speed and magnitude of price changes. Values above 70 indicate overbought conditions, below 30 indicate oversold.",
    macd: "Moving Average Convergence Divergence shows the relationship between two moving averages. When MACD crosses above signal line, it may indicate bullish momentum.",
    bollingerBands: "Bollinger Bands consist of a middle band (SMA) and two outer bands (standard deviations). Price touching outer bands may indicate overbought/oversold conditions.",
    volume: "Volume analysis compares current trading volume to average volume. High volume with price movement can confirm trend strength.",
    trends: "Trend analysis examines price direction over different timeframes using moving averages to determine short, medium, and long-term market sentiment."
  };

  const InfoTooltip = ({ type, children }) => (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setShowTooltip(showTooltip === type ? null : type)}
        style={{
          background: 'rgba(107, 114, 128, 0.2)',
          border: '1px solid rgba(107, 114, 128, 0.3)',
          borderRadius: '50%',
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: '#6b7280',
          fontSize: '12px',
          marginLeft: '0.5rem'
        }}
      >
        <Info size={12} />
      </button>
      {showTooltip === type && (
        <div style={{
          position: 'absolute',
          top: '25px',
          left: '-150px',
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '0.75rem',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          width: '300px',
          fontSize: '0.875rem',
          color: '#374151',
          lineHeight: '1.4'
        }}>
          {tooltipInfo[type]}
        </div>
      )}
    </div>
  );

  if (loading) {
    return createPortal(
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 9999
      }} onClick={onClose}>
        <div style={{
          backgroundColor: 'white', borderRadius: '12px', padding: '2rem',
          textAlign: 'center', minWidth: '300px'
        }} onClick={(e) => e.stopPropagation()}>
          <Activity size={32} style={{ marginBottom: '1rem', animation: 'spin 1s linear infinite' }} />
          <p>Calculating technical indicators for {symbol}...</p>
          <button onClick={onClose} style={{
            position: 'absolute', top: '1rem', right: '1rem',
            background: 'none', border: 'none', fontSize: '1.5rem',
            cursor: 'pointer'
          }}>×</button>
        </div>
      </div>,
      document.body
    );
  }

  if (error) {
    return createPortal(
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 9999
      }} onClick={onClose}>
        <div style={{
          backgroundColor: 'white', borderRadius: '12px', padding: '2rem',
          textAlign: 'center', minWidth: '300px'
        }} onClick={(e) => e.stopPropagation()}>
          <AlertTriangle size={32} color="#ef4444" style={{ marginBottom: '1rem' }} />
          <p>Error: {error}</p>
          <button onClick={onClose} style={{
            position: 'absolute', top: '1rem', right: '1rem',
            background: 'none', border: 'none', fontSize: '1.5rem',
            cursor: 'pointer'
          }}>×</button>
        </div>
      </div>,
      document.body
    );
  }

  if (!technicalData) {
    return createPortal(
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 9999
      }} onClick={onClose}>
        <div style={{
          backgroundColor: 'white', borderRadius: '12px', padding: '2rem',
          textAlign: 'center', minWidth: '300px'
        }} onClick={(e) => e.stopPropagation()}>
          <Activity size={32} style={{ marginBottom: '1rem', animation: 'spin 1s linear infinite' }} />
          <p>Loading technical analysis...</p>
          <button onClick={onClose} style={{
            position: 'absolute', top: '1rem', right: '1rem',
            background: 'none', border: 'none', fontSize: '1.5rem',
            cursor: 'pointer'
          }}>×</button>
        </div>
      </div>,
      document.body
    );
  }

  const modalContent = (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 9999
    }} onClick={onClose}>
      <div style={{
        backgroundColor: 'white', borderRadius: '12px',
        maxWidth: '1200px', width: '90vw', maxHeight: '90vh',
        overflowY: 'auto', position: 'relative'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{
          background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
          color: 'white', padding: '1.5rem 2rem',
          borderRadius: '12px 12px 0 0', position: 'relative'
        }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600, paddingRight: '3rem' }}>
            {t('technicalAnalysis')} - {symbol}
          </h2>
          <button onClick={onClose} style={{
            position: 'absolute', top: '1rem', right: '1rem',
            width: '40px', height: '40px', borderRadius: '50%',
            border: 'none', backgroundColor: '#f3f4f6', color: '#6b7280',
            cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center'
          }}>
            <X size={20} />
          </button>
        </div>

        <div className="technical-content" style={{ padding: '1.5rem' }}>
          {/* Trend Analysis */}
          <div className="trend-section" style={{ marginBottom: '2rem' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <Signal size={18} /> Trend Analysis
              <InfoTooltip type="trends" />
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
              <div className="trend-card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Short-term (20 days)</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {getTrendIcon(technicalData.trends.shortTerm)}
                    <span style={{ textTransform: 'capitalize', fontWeight: '600' }}>
                      {technicalData.trends.shortTerm}
                    </span>
                  </div>
                </div>
              </div>
              <div className="trend-card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Medium-term (50 days)</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {getTrendIcon(technicalData.trends.mediumTerm)}
                    <span style={{ textTransform: 'capitalize', fontWeight: '600' }}>
                      {technicalData.trends.mediumTerm}
                    </span>
                  </div>
                </div>
              </div>
              <div className="trend-card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Long-term (200 days)</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {getTrendIcon(technicalData.trends.longTerm)}
                    <span style={{ textTransform: 'capitalize', fontWeight: '600' }}>
                      {technicalData.trends.longTerm}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Technical Indicators */}
          <div className="indicators-section" style={{ marginBottom: '2rem' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <BarChart3 size={18} /> {t('technicalIndicators')}
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
              
              {/* Moving Averages */}
              <div className="indicator-section">
                <h4 style={{ display: 'flex', alignItems: 'center' }}>
                  {t('movingAverages')}
                  <InfoTooltip type="sma" />
                </h4>
                <div className="indicator-row">
                  <span>{t('price')}</span>
                  <span style={{ fontWeight: '600' }}>${formatNumber(technicalData.currentPrice)}</span>
                </div>
                <div className="indicator-row">
                  <span>{t('sma20')}</span>
                  <span style={{ color: technicalData.currentPrice > technicalData.indicators.sma20 ? '#10b981' : '#ef4444' }}>
                    ${formatNumber(technicalData.indicators.sma20)}
                  </span>
                </div>
                <div className="indicator-row">
                  <span>{t('sma50')}</span>
                  <span style={{ color: technicalData.currentPrice > technicalData.indicators.sma50 ? '#10b981' : '#ef4444' }}>
                    ${formatNumber(technicalData.indicators.sma50)}
                  </span>
                </div>
                <div className="indicator-row">
                  <span>{t('sma200')}</span>
                  <span style={{ color: technicalData.currentPrice > technicalData.indicators.sma200 ? '#10b981' : '#ef4444' }}>
                    ${formatNumber(technicalData.indicators.sma200)}
                  </span>
                </div>
              </div>

              {/* Momentum Indicators */}
              <div className="indicator-section">
                <h4 style={{ display: 'flex', alignItems: 'center' }}>
                  {t('momentum')}
                  <InfoTooltip type="rsi" />
                </h4>
                <div className="indicator-row">
                  <span>{t('rsiIndicator')} (14)</span>
                  <span style={{ 
                    color: technicalData.indicators.rsi > 70 ? '#ef4444' : 
                           technicalData.indicators.rsi < 30 ? '#10b981' : '#6b7280'
                  }}>
                    {formatNumber(technicalData.indicators.rsi)}
                  </span>
                </div>
                <div className="indicator-row">
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    {t('macdIndicator')}
                    <InfoTooltip type="macd" />
                  </span>
                  <span style={{ color: technicalData.indicators.macd.histogram > 0 ? '#10b981' : '#ef4444' }}>
                    {formatNumber(technicalData.indicators.macd.macd, 4)}
                  </span>
                </div>
                <div className="indicator-row">
                  <span>{t('macdIndicator')} Signal</span>
                  <span>{formatNumber(technicalData.indicators.macd.signal, 4)}</span>
                </div>
                <div className="indicator-row">
                  <span>{t('macdIndicator')} Histogram</span>
                  <span style={{ color: technicalData.indicators.macd.histogram > 0 ? '#10b981' : '#ef4444' }}>
                    {formatNumber(technicalData.indicators.macd.histogram, 4)}
                  </span>
                </div>
              </div>

              {/* Volatility Indicators */}
              <div className="indicator-section">
                <h4 style={{ display: 'flex', alignItems: 'center' }}>
                  {t('bollingerBands')}
                  <InfoTooltip type="bollingerBands" />
                </h4>
                <div className="indicator-row">
                  <span>Upper Band</span>
                  <span>${formatNumber(technicalData.indicators.bollingerBands.upper)}</span>
                </div>
                <div className="indicator-row">
                  <span>Middle Band ({t('sma20')})</span>
                  <span>${formatNumber(technicalData.indicators.bollingerBands.middle)}</span>
                </div>
                <div className="indicator-row">
                  <span>Lower Band</span>
                  <span>${formatNumber(technicalData.indicators.bollingerBands.lower)}</span>
                </div>
                <div className="indicator-row">
                  <span>Position</span>
                  <span style={{ 
                    color: technicalData.currentPrice > technicalData.indicators.bollingerBands.upper ? '#ef4444' :
                           technicalData.currentPrice < technicalData.indicators.bollingerBands.lower ? '#10b981' : '#6b7280'
                  }}>
                    {technicalData.currentPrice > technicalData.indicators.bollingerBands.upper ? 'Above Upper' :
                     technicalData.currentPrice < technicalData.indicators.bollingerBands.lower ? 'Below Lower' : 'Within Bands'}
                  </span>
                </div>
              </div>

              {/* Volume Analysis */}
              <div className="indicator-section">
                <h4 style={{ display: 'flex', alignItems: 'center' }}>
                  Volume Analysis
                  <InfoTooltip type="volume" />
                </h4>
                <div className="indicator-row">
                  <span>Current Volume</span>
                  <span>{technicalData.indicators.currentVolume ? technicalData.indicators.currentVolume.toLocaleString() : 'N/A'}</span>
                </div>
                <div className="indicator-row">
                  <span>Volume MA (20)</span>
                  <span>{technicalData.indicators.volumeMA ? technicalData.indicators.volumeMA.toLocaleString() : 'N/A'}</span>
                </div>
                <div className="indicator-row">
                  <span>Volume vs Average</span>
                  <span style={{ 
                    color: technicalData.indicators.currentVolume > technicalData.indicators.volumeMA ? '#10b981' : '#ef4444'
                  }}>
                    {technicalData.indicators.currentVolume && technicalData.indicators.volumeMA ? 
                      `${((technicalData.indicators.currentVolume / technicalData.indicators.volumeMA - 1) * 100).toFixed(1)}%` : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Trading Signals */}
          <div className="signals-section">
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <Zap size={18} /> {t('tradingSignals')}
            </h3>
            {technicalData.signals.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {technicalData.signals.map((signal, index) => (
                  <div key={index} style={{
                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                    padding: '0.75rem', borderRadius: '6px',
                    backgroundColor: signal.type === 'bullish' ? '#ecfdf5' : '#fef2f2',
                    border: `1px solid ${signal.type === 'bullish' ? '#a7f3d0' : '#fecaca'}`
                  }}>
                    {signal.type === 'bullish' ? 
                      <TrendingUp size={16} color="#10b981" /> : 
                      <TrendingDown size={16} color="#ef4444" />}
                    <div>
                      <div style={{ 
                        fontWeight: '600', 
                        color: signal.type === 'bullish' ? '#065f46' : '#991b1b',
                        fontSize: '0.875rem'
                      }}>
                        {signal.indicator}
                      </div>
                      <div style={{ 
                        color: signal.type === 'bullish' ? '#047857' : '#dc2626',
                        fontSize: '0.8rem'
                      }}>
                        {t(signal.messageKey)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '2rem', 
                color: '#6b7280',
                fontStyle: 'italic'
              }}>
                {t('noSignals')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default React.memo(TechnicalAnalysis);
